import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Calendar, MapPin, Users, CheckCircle, XCircle, CalendarX2 } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import Drawer from '../../components/common/Drawer/Drawer';
import api from '../../api/axios';
import styles from './ResidentEvents.module.css';

const ResidentEvents = () => {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [rsvpStatus, setRsvpStatus] = useState({});
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/community/events');
      setEvents(data);
      
      // Calculate initial RSVP status from attendees array
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      if (userInfo._id) {
        const statuses = {};
        data.forEach(evt => {
          if (evt.attendees.includes(userInfo._id)) {
            statuses[evt._id] = 'yes';
          }
        });
        setRsvpStatus(statuses);
      }
      
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const handleRSVP = async (eventId, status) => {
    try {
      await api.post(`/community/events/${eventId}/rsvp`);
      setRsvpStatus(prev => ({ ...prev, [eventId]: status }));
      
      // Update local events array count
      setEvents(events.map(ev => {
        if (ev._id === eventId) {
          const inc = status === 'yes' ? 1 : -1;
          return { ...ev, attendees: [...ev.attendees, 'dummy'] }; // just for UI update
        }
        return ev;
      }));
      
      setSelectedEvent(null);
      toast.success('RSVP updated successfully!');
    } catch (err) {
      toast.error("Failed to update RSVP");
    }
  };

  return (
    <div className={styles.container}>
      <PageHeader 
        title="Upcoming Events" 
        breadcrumb={['Community', 'Events']} 
      />

      <div className={styles.grid}>
        {loading ? <p>Loading events...</p> : events.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem 2rem', background: '#fff', borderRadius: '12px', border: '1px dashed #d1d5db', gridColumn: '1 / -1' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '64px', height: '64px', borderRadius: '50%', background: '#fef2f2', color: '#b91c1c', marginBottom: '1rem' }}>
              <CalendarX2 size={32} />
            </div>
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#111', fontSize: '18px' }}>No Upcoming Events</h3>
            <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>There are currently no upcoming events scheduled in the society.</p>
          </div>
        ) : events.map(event => {
          const dateObj = new Date(event.date);
          const day = !isNaN(dateObj) ? dateObj.getDate().toString().padStart(2, '0') : event.date.split(' ')[0] || '';
          const month = !isNaN(dateObj) ? dateObj.toLocaleString('default', { month: 'short' }).toUpperCase() : (event.date.split(' ')[1] ? event.date.split(' ')[1].replace(',', '') : '');

          return (
          <div key={event._id} className={styles.card} onClick={() => setSelectedEvent(event)}>
            <div className={styles.imageContainer}>
              <img src={event.image ? (event.image.startsWith('http') ? event.image : `${import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '')}${event.image}`) : 'https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?q=80&w=600&auto=format&fit=crop'} alt={event.title} className={styles.eventImage} />
              <div className={styles.dateBadge}>
                <strong>{day}</strong>
                <span>{month}</span>
              </div>
            </div>
            
            <div className={styles.cardBody}>
              <h3>{event.title}</h3>
              <div className={styles.infoRow}>
                <Calendar size={14} />
                <span>{event.time}</span>
              </div>
              <div className={styles.infoRow}>
                <MapPin size={14} />
                <span>{event.location}</span>
              </div>
            </div>
            
            <div className={styles.cardFooter}>
              <div className={styles.attendees}>
                <Users size={14} />
                <span>{event.attendees.length} Attending</span>
              </div>
              
              <span className={`${styles.rsvpBadge} ${styles[rsvpStatus[event._id] || 'pending']}`}>
                {rsvpStatus[event._id] === 'yes' ? 'Going' : rsvpStatus[event._id] === 'no' ? 'Not Going' : 'RSVP Pending'}
              </span>
            </div>
          </div>
        )})}
      </div>

      <Drawer 
        isOpen={!!selectedEvent} 
        onClose={() => setSelectedEvent(null)} 
        title="Event Details"
      >
        {selectedEvent && (
          <div className={styles.eventDetail}>
            <img src={selectedEvent.image ? (selectedEvent.image.startsWith('http') ? selectedEvent.image : `${import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '')}${selectedEvent.image}`) : 'https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?q=80&w=600&auto=format&fit=crop'} alt={selectedEvent.title} className={styles.detailImage} />
            
            <h2 className={styles.detailTitle}>{selectedEvent.title}</h2>
            
            <div className={styles.detailGrid}>
              <div className={styles.detailBox}>
                <Calendar size={16} />
                <div>
                  <span>Date & Time</span>
                  <strong>{selectedEvent.date}, {selectedEvent.time}</strong>
                </div>
              </div>
              <div className={styles.detailBox}>
                <MapPin size={16} />
                <div>
                  <span>Location</span>
                  <strong>{selectedEvent.location}</strong>
                </div>
              </div>
            </div>

            <div className={styles.detailDescription}>
              <h3>About this event</h3>
              <p>{selectedEvent.description}</p>
            </div>
            
            <div className={styles.rsvpSection}>
              <h3>Are you going?</h3>
              <div className={styles.rsvpButtons}>
                <button 
                  className={`${styles.btnYes} ${rsvpStatus[selectedEvent._id] === 'yes' ? styles.activeYes : ''}`}
                  onClick={() => handleRSVP(selectedEvent._id, 'yes')}
                >
                  <CheckCircle size={18} /> Yes, I'm going
                </button>
                <button 
                  className={`${styles.btnNo} ${rsvpStatus[selectedEvent._id] === 'no' ? styles.activeNo : ''}`}
                  onClick={() => handleRSVP(selectedEvent._id, 'no')}
                >
                  <XCircle size={18} /> No, maybe next time
                </button>
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default ResidentEvents;
